import * as React from 'react';

import {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';

/**
 * `pager` des maquettes : « 1 à 25 sur 120 requêtes » à gauche (+ choix du
 * nombre par page), boutons de page à droite. Pagination locale d'une liste
 * déjà chargée (`usePagination`) : aucun paramètre n'est envoyé à l'API.
 */

interface PagerProps {
  /** Page courante, à partir de 1. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Nom des éléments au pluriel : « requêtes », « clients ». */
  itemLabel: string;
  /** Choix du nombre par page ; masqué si absent. */
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (pageSize: number) => void;
  /** Boutons première / dernière page (listes longues). */
  showFirstLast?: boolean;
  className?: string;
}

/** Numéros de page à afficher : tous jusqu'à 7 pages, sinon fenêtre autour de la page courante. */
function pageWindow(page: number, pages: number): (number | 'gap')[] {
  if (pages <= 7) {
    return Array.from({ length: pages }, (_, index) => index + 1);
  }
  const result: (number | 'gap')[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(pages - 1, page + 1);
  if (start > 2) {
    result.push('gap');
  }
  for (let current = start; current <= end; current += 1) {
    result.push(current);
  }
  if (end < pages - 1) {
    result.push('gap');
  }
  result.push(pages);
  return result;
}

function Pager({
  page,
  pageSize,
  total,
  onPageChange,
  itemLabel,
  pageSizeOptions,
  onPageSizeChange,
  showFirstLast = false,
  className,
}: PagerProps): React.JSX.Element {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const sizeId = React.useId();
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2 text-caption text-muted-foreground',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <span aria-live="polite">
          {from} à {to} sur {total} {itemLabel}
        </span>
        {pageSizeOptions !== undefined && onPageSizeChange !== undefined ? (
          <>
            <label htmlFor={sizeId} className="sr-only">
              Nombre par page
            </label>
            <select
              id={sizeId}
              value={pageSize}
              onChange={(event) => {
                onPageSizeChange(Number(event.target.value));
              }}
              className="h-6 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option} par page
                </option>
              ))}
            </select>
          </>
        ) : null}
      </div>
      {pages > 1 ? (
        <Pagination>
          <PaginationContent>
            {showFirstLast ? (
              <PaginationItem>
                <PaginationFirst
                  disabled={page <= 1}
                  onClick={() => {
                    onPageChange(1);
                  }}
                />
              </PaginationItem>
            ) : null}
            <PaginationItem>
              <PaginationPrevious
                disabled={page <= 1}
                onClick={() => {
                  onPageChange(page - 1);
                }}
              />
            </PaginationItem>
            {pageWindow(page, pages).map((entry, index) =>
              entry === 'gap' ? (
                <PaginationItem key={`gap-${index}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={entry}>
                  <PaginationButton
                    isActive={entry === page}
                    aria-label={`Page ${entry}`}
                    onClick={() => {
                      onPageChange(entry);
                    }}
                  >
                    {entry}
                  </PaginationButton>
                </PaginationItem>
              ),
            )}
            <PaginationItem>
              <PaginationNext
                disabled={page >= pages}
                onClick={() => {
                  onPageChange(page + 1);
                }}
              />
            </PaginationItem>
            {showFirstLast ? (
              <PaginationItem>
                <PaginationLast
                  disabled={page >= pages}
                  onClick={() => {
                    onPageChange(pages);
                  }}
                />
              </PaginationItem>
            ) : null}
          </PaginationContent>
        </Pagination>
      ) : null}
    </div>
  );
}

interface PaginationState<T> {
  /** Éléments de la page courante. */
  pageItems: T[];
  page: number;
  pageSize: number;
  total: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  /** Props prêtes pour `<Pager>` (ajouter `itemLabel`). */
  pagerProps: Pick<PagerProps, 'page' | 'pageSize' | 'total' | 'onPageChange' | 'onPageSizeChange'>;
}

/**
 * Pagination locale d'une liste déjà chargée. La page revient dans les bornes
 * quand la liste raccourcit (filtre, suppression).
 */
function usePagination<T>(items: readonly T[], initialPageSize = 25): PaginationState<T> {
  const [requestedPage, setPage] = React.useState(1);
  const [pageSize, setPageSizeState] = React.useState(initialPageSize);
  const total = items.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(Math.max(1, requestedPage), pages);
  const pageItems = React.useMemo(
    () => items.slice((page - 1) * pageSize, page * pageSize),
    [items, page, pageSize],
  );
  const setPageSize = React.useCallback((next: number): void => {
    setPageSizeState(next);
    setPage(1);
  }, []);
  return {
    pageItems,
    page,
    pageSize,
    total,
    setPage,
    setPageSize,
    pagerProps: { page, pageSize, total, onPageChange: setPage, onPageSizeChange: setPageSize },
  };
}

export { Pager, usePagination, pageWindow };
export type { PagerProps, PaginationState };
